// src/ui/components/omrSheet.ts — Optical Mark Recognition (OMR) Bubble Answer Sheet (T-12.1.3, FR-MOCK-002)
// Simulates the authentic Bexley Selection Test answer grid with tactile tap-to-shade bubbles,
// unselect toggling, question jumping, and unanswered counts.

import { tokens } from '../designTokens';

export interface OmrSheetOptions {
  totalQuestions: number;
  questionIds: string[];
  currentQuestionIndex?: number;
  initialAnswers?: Record<string, number | null>;
  onSelectAnswer?: (questionIndex: number, optionIndex: number | null) => void;
  onJumpToQuestion?: (questionIndex: number) => void;
  readOnly?: boolean;
}

export class OmrSheet {
  private container: HTMLElement;
  private totalQuestions: number;
  private questionIds: string[];
  private currentQuestionIndex: number;
  private answers: Record<string, number | null>;
  private onSelectAnswer?: (questionIndex: number, optionIndex: number | null) => void;
  private onJumpToQuestion?: (questionIndex: number) => void;
  private readOnly: boolean;

  constructor(options: OmrSheetOptions) {
    this.totalQuestions = options.totalQuestions;
    this.questionIds = options.questionIds;
    this.currentQuestionIndex = options.currentQuestionIndex ?? 0;
    this.answers = { ...(options.initialAnswers || {}) };
    this.onSelectAnswer = options.onSelectAnswer;
    this.onJumpToQuestion = options.onJumpToQuestion;
    this.readOnly = options.readOnly ?? false;

    // Ensure all questionIds have a key
    for (const qId of this.questionIds) {
      if (this.answers[qId] === undefined) {
        this.answers[qId] = null;
      }
    }

    this.container = document.createElement('div');
    this.container.className = 'omr-sheet-container';
    this.render();
  }

  getElement(): HTMLElement {
    return this.container;
  }

  setAnswer(questionIndex: number, optionIndex: number | null): void {
    const qId = this.questionIds[questionIndex];
    if (!qId) return;
    this.answers[qId] = optionIndex;
    this.updateRow(questionIndex);
    this.updateCounters();
  }

  setCurrentQuestion(questionIndex: number): void {
    const prevIndex = this.currentQuestionIndex;
    this.currentQuestionIndex = questionIndex;
    this.updateRow(prevIndex);
    this.updateRow(questionIndex);
  }

  getAnswers(): Record<string, number | null> {
    return { ...this.answers };
  }

  getAnsweredCount(): number {
    return Object.values(this.answers).filter((val) => val !== null && val !== undefined).length;
  }

  getUnansweredCount(): number {
    return this.totalQuestions - this.getAnsweredCount();
  }

  private render(): void {
    const answeredCount = this.getAnsweredCount();
    const unansweredCount = this.totalQuestions - answeredCount;

    this.container.innerHTML = `
      <section class="omr-card" style="
        background: #FFFDF9;
        border: 2px solid ${tokens.color.morrisIndigo};
        border-radius: 16px;
        padding: 20px 16px;
        box-shadow: 0 4px 20px rgba(7, 20, 36, 0.1);
        max-width: 480px;
        width: 100%;
        font-family: ${tokens.font.mono};
      ">
        <!-- OMR Technical Header -->
        <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid ${tokens.color.morrisIndigo}; padding-bottom: 12px; margin-bottom: 14px;">
          <div>
            <div style="font-size: 0.75rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase; letter-spacing: 0.05em;">
              BEXLEY SELECTION TEST · OMR ANSWER SHEET
            </div>
            <div style="font-family: ${tokens.font.heading}; font-size: 1.15rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">
              Candidate Response Grid
            </div>
            <div style="font-size: 0.75rem; color: #5A6D86; margin-top: 2px;">
              Tap bubble to shade. Tap again to clear.
            </div>
          </div>

          <!-- Decorative OMR Alignment Crosshairs -->
          <div style="display: flex; gap: 4px; align-items: center;" title="Optical alignment calibration mark">
            <div style="width: 12px; height: 12px; border: 1.5px solid ${tokens.color.morrisIndigo}; position: relative;">
              <div style="position: absolute; top: 4px; left: 0; right: 0; height: 1.5px; background: ${tokens.color.morrisIndigo};"></div>
              <div style="position: absolute; left: 4px; top: 0; bottom: 0; width: 1.5px; background: ${tokens.color.morrisIndigo};"></div>
            </div>
            <div style="font-size: 0.7rem; font-weight: 700; color: ${tokens.color.morrisIndigo};">CAL-01</div>
          </div>
        </div>

        <!-- Telemetry Status Bar -->
        <div id="omr-counters-bar" style="display: flex; justify-content: space-between; align-items: center; background: rgba(250, 246, 237, 0.9); border: 1px solid ${tokens.color.gildedBrass}; border-radius: 10px; padding: 8px 12px; margin-bottom: 14px; font-size: 0.85rem; font-weight: 700;">
          <span style="color: ${tokens.color.morrisIndigo};">
            Answered: <strong id="omr-count-answered" style="color: ${tokens.color.seaweedSage};">${answeredCount}</strong> / ${this.totalQuestions}
          </span>
          <span style="color: ${unansweredCount > 0 ? tokens.color.madderCoral : tokens.color.seaweedSage};">
            Unanswered: <strong id="omr-count-unanswered">${unansweredCount}</strong>
          </span>
        </div>

        <!-- Scrollable OMR Bubble Grid (Two columns for tablet/desktop) -->
        <div id="omr-bubble-grid" style="
          max-height: 480px;
          overflow-y: auto;
          padding-right: 6px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        ">
          ${this.questionIds.map((qId, idx) => this.renderRowMarkup(idx, qId)).join('')}
        </div>
      </section>
    `;

    this.attachEventListeners();
  }

  private renderRowMarkup(idx: number, qId: string): string {
    const isCurrent = idx === this.currentQuestionIndex;
    const selectedAns = this.answers[qId];
    const isAnswered = selectedAns !== null && selectedAns !== undefined;
    const letters = ['A', 'B', 'C', 'D', 'E'];

    return `
      <div
        class="omr-row ${isCurrent ? 'omr-row-current' : ''}"
        data-index="${idx}"
        id="omr-row-${idx}"
        style="
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 6px 10px;
          border-radius: 8px;
          background: ${isCurrent ? 'rgba(229, 169, 60, 0.18)' : isAnswered ? 'rgba(61, 90, 69, 0.05)' : '#FFFDF9'};
          border: 1.5px solid ${isCurrent ? tokens.color.gildedBrass : 'rgba(26, 54, 93, 0.15)'};
          transition: background 0.15s ease;
        "
      >
        <!-- Question Number (Tap to Jump) -->
        <button
          type="button"
          class="omr-qnum-btn touch-target"
          data-jump="${idx}"
          style="
            border: none;
            background: none;
            cursor: pointer;
            font-family: ${tokens.font.mono};
            font-size: 0.95rem;
            font-weight: 800;
            color: ${isCurrent ? tokens.color.morrisIndigo : '#4A5B73'};
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 4px 6px;
            min-height: 48px;
            min-width: 48px;
          "
          title="Jump to question ${idx + 1}"
        >
          <span>Q${String(idx + 1).padStart(2, '0')}</span>
          ${isAnswered ? '<span style="color: ' + tokens.color.seaweedSage + '; font-size: 0.8rem;">●</span>' : '<span style="color: ' + tokens.color.madderCoral + '; font-size: 0.8rem;">○</span>'}
        </button>

        <!-- 5 Optical Mark Bubbles [A] [B] [C] [D] [E] -->
        <div style="display: flex; gap: 6px; align-items: center;">
          ${letters.map((letter, optIdx) => {
            const isShaded = selectedAns === optIdx;
            return `
              <button
                type="button"
                class="omr-bubble touch-target"
                data-row="${idx}"
                data-option="${optIdx}"
                style="
                  width: 32px;
                  height: 32px;
                  min-width: 32px;
                  min-height: 32px;
                  border-radius: 50%;
                  border: 2px solid ${isShaded ? tokens.color.morrisIndigo : 'rgba(26, 54, 93, 0.4)'};
                  background: ${isShaded ? tokens.color.morrisIndigo : '#FFFDF9'};
                  color: ${isShaded ? '#FFFDF9' : tokens.color.morrisIndigo};
                  font-size: 0.85rem;
                  font-weight: 800;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  cursor: ${this.readOnly ? 'default' : 'pointer'};
                  padding: 0;
                  transition: all 0.15s cubic-bezier(0.16, 1, 0.3, 1);
                  box-shadow: ${isShaded ? 'inset 0 2px 4px rgba(0,0,0,0.4), 0 2px 4px rgba(7, 20, 36, 0.15)' : 'none'};
                "
                aria-label="Question ${idx + 1} option ${letter} ${isShaded ? 'selected' : 'unselected'}"
                ${this.readOnly ? 'disabled' : ''}
              >
                ${letter}
              </button>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  private updateRow(idx: number): void {
    const qId = this.questionIds[idx];
    if (!qId) return;
    const rowEl = this.container.querySelector<HTMLElement>(`#omr-row-${idx}`);
    if (rowEl) {
      rowEl.outerHTML = this.renderRowMarkup(idx, qId);
      this.attachRowListeners(idx);
    }
  }

  private updateCounters(): void {
    const answeredCount = this.getAnsweredCount();
    const unansweredCount = this.totalQuestions - answeredCount;

    const answeredEl = this.container.querySelector<HTMLElement>('#omr-count-answered');
    if (answeredEl) answeredEl.textContent = String(answeredCount);

    const unansweredEl = this.container.querySelector<HTMLElement>('#omr-count-unanswered');
    if (unansweredEl) {
      unansweredEl.textContent = String(unansweredCount);
      unansweredEl.style.color = unansweredCount > 0 ? tokens.color.madderCoral : tokens.color.seaweedSage;
    }
  }

  private attachEventListeners(): void {
    this.questionIds.forEach((_, idx) => {
      this.attachRowListeners(idx);
    });
  }

  private attachRowListeners(idx: number): void {
    const row = this.container.querySelector<HTMLElement>(`#omr-row-${idx}`);
    if (!row) return;

    // Jump button
    const jumpBtn = row.querySelector<HTMLButtonElement>('.omr-qnum-btn');
    jumpBtn?.addEventListener('click', () => {
      if (this.onJumpToQuestion) {
        this.onJumpToQuestion(idx);
      }
    });

    // Bubbles
    if (!this.readOnly) {
      const bubbles = row.querySelectorAll<HTMLButtonElement>('.omr-bubble');
      bubbles.forEach((b) => {
        b.addEventListener('click', () => {
          const optIdx = Number(b.getAttribute('data-option'));
          const qId = this.questionIds[idx];
          if (!qId) return;

          const currentVal = this.answers[qId];
          // Tap again to clear/deselect
          const newVal = currentVal === optIdx ? null : optIdx;
          this.answers[qId] = newVal;

          this.updateRow(idx);
          this.updateCounters();

          if (this.onSelectAnswer) {
            this.onSelectAnswer(idx, newVal);
          }
        });
      });
    }
  }
}
